import React, { useEffect, useMemo, useRef, useState } from 'react'
import './ComboInput.css'

/**
 * Generisk ComboInput - kombination af tekstfelt og dropdown med forslag.
 * Bruger lokal state under indtastning for at undgå fokus-tab ved hvert tastetryk.
 * Synkroniserer til parent ved blur og ved valg fra listen.
 *
 * @param {string} value - Kontrolleret værdi
 * @param {function} onChange - Kaldes med ny værdi ved blur eller valg
 * @param {string[]} options - Liste af forslag
 * @param {string} placeholder - Placeholder-tekst
 * @param {string} ariaLabel - Aria-label for tilgængelighed
 * @param {string} id - Id til form-label (valgfri)
 * @param {string} className - Ekstra CSS-klasse
 * @param {string} emptyMessage - Tekst når ingen forslag matcher (default: "Ingen forslag")
 * @param {boolean} disabled - Deaktiver input
 * @param {function} onInputChange - Kaldes ved hvert tastetryk (valgfri)
 */
function ComboInput({
  value,
  onChange,
  onInputChange,
  options = [],
  placeholder = '',
  ariaLabel,
  id,
  className = '',
  emptyMessage = 'Ingen forslag',
  disabled = false,
}) {
  const wrapRef = useRef(null)
  const [localValue, setLocalValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const normalizedQuery = (localValue || '').toString().toLowerCase()
  const filtered = useMemo(() => {
    if (showAll) return options
    if (!normalizedQuery) return options
    const matches = options.filter((o) => o.toLowerCase().includes(normalizedQuery))
    if (matches.length === 0 && normalizedQuery && !options.some((o) => o.toLowerCase() === normalizedQuery)) {
      return [localValue]
    }
    return matches
  }, [options, normalizedQuery, showAll, localValue])

  useEffect(() => {
    if (!isOpen) return
    const onDocDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [isOpen])

  const commitValue = (v) => {
    setLocalValue(v)
    onChange(v)
  }

  return (
    <div className={`combo-input ${className}`.trim()} ref={wrapRef}>
      <input
        id={id}
        className="combo-input__input"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value)
          onInputChange?.(e.target.value)
          setIsOpen(true)
          setShowAll(false)
        }}
        onBlur={() => commitValue(localValue)}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        disabled={disabled}
      />
      <button
        type="button"
        className="combo-input__toggle"
        aria-label="Vis liste"
        onClick={() => {
          const willOpen = !isOpen
          setIsOpen(willOpen)
          setShowAll(willOpen)
        }}
        disabled={disabled}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {isOpen ? (
        <div className="combo-input__list" role="listbox">
          {filtered.length === 0 ? (
            <div className="combo-input__empty">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o}
                type="button"
                className="combo-input__item"
                role="option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  commitValue(o)
                  setIsOpen(false)
                }}
              >
                {o}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

export default ComboInput
